import { useCallback } from "react";
import { Auth } from "shared/@auth/Auth";
import { useQuery, useMutation, useQueryClient } from "react-query";
import config from "shared/regionConfig";

export function useTopics(
  iboNumber,
  didCreateTopicsAccount,
  didRefetch,
  didRetrieveGroupUsers,
  didRetrieveThreadUsers,
  didRetrieveAccessCode
) {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  const { data, error, isLoading } = useQuery(
    ["TopicsUser", iboNumber],
    async () => {
      if (!iboNumber) {
        throw new Error("iboNumber is required to fetch topics data.");
      }
      const res = await makeAuthedRequest(`/v1/topics/user/${iboNumber}`);

      if (res.ok) {
        return res.json();
      }
      return { iboA: null, iboB: null };
    },
    { enabled: !!iboNumber }
  );

  const addTopicsAccount = useCallback(
    async (contact) => {
      const res = await makeAuthedRequest(`/v1/topics/user/create`, {
        method: "POST",
        body: JSON.stringify({
          iboNumber: contact.iboNumber,
          wwgId: contact.wwgId,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
        }),
      });

      if (res.status === 201) {
        didCreateTopicsAccount(contact);
      }
    },
    [makeAuthedRequest, didCreateTopicsAccount]
  );

  const refetch = useCallback(async () => {
    try {
      const res = await makeAuthedRequest(`/v1/topics/user/${iboNumber}`);
      if (res.ok) {
        const data = await res.json();
        queryClient.setQueryData(["TopicsUser", iboNumber], data); // Update the cache
        didRefetch(data); // Callback to handle new data
        return data;
      }
      throw new Error(`Failed to refetch: ${await res.text()}`);
    } catch (error) {
      console.error("Error refetching topics data:", error);
      throw error;
    }
  }, [iboNumber, makeAuthedRequest, queryClient, didRefetch]);

  // Sync Topics Account
  const syncTopicsAccount = useCallback(
    async (contact) => {
      const res = await makeAuthedRequest(`/v1/topics/sync`, {
        method: "POST",
        body: JSON.stringify({
          type: "ibo",
          iboNumber: contact.wwgInfo.iboNumber,
          wwgId: contact.wwgInfo.wwgId,
        }),
      });

      if (!res.ok) {
        console.error("Error calling sync API");
      }
    },
    [makeAuthedRequest]
  );

  // Fetch IBO Sync Data
  const fetchIboSyncData = useCallback(
    async (contact) => {
      try {
        const res = await makeAuthedRequest(
          `/v1/topics/sync/ibo/${contact.wwgInfo.iboNumber}/${contact.wwgInfo.wwgId}`
        );

        if (res.ok) {
          return res.json();
        }
        throw new Error(await res.text());
      } catch (error) {
        console.error("Error fetching IBO sync data:", error);
      }
    },
    [makeAuthedRequest]
  );

  // Retrieve Group Users
  const retrieveGroupUsers = useCallback(
    async (userId, groupId) => {
      try {
        const response = await makeAuthedRequest(
          `/v1/topics/group-users/${userId}/${groupId}`
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Failed to retrieve group users: ${errorText}`);
          throw new Error(errorText);
        }

        const data = await response.json();
        didRetrieveGroupUsers(data);
      } catch (error) {
        console.error("Error fetching group users:", error);
      }
    },
    [makeAuthedRequest, didRetrieveGroupUsers]
  );

  // Retrieve Thread Users
  const retrieveThreadUsers = useCallback(
    async (userId, threadId) => {
      try {
        const response = await makeAuthedRequest(
          `/v1/topics/thread-users/${userId}/${threadId}`
        );

        if (response.ok) {
          const data = await response.json();
          didRetrieveThreadUsers(data);
        } else {
          const errorText = await response.text();
          console.error(`Failed to retrieve thread users: ${errorText}`);
          throw new Error(errorText);
        }
      } catch (error) {
        console.error("Error fetching thread users:", error);
      }
    },
    [makeAuthedRequest, didRetrieveThreadUsers]
  );

  // Cancel Sync Put
  const cancelSyncPut = useMutation((syncId) =>
    makeAuthedRequest(`/v1/topics/sync/complete`, {
      method: "PUT",
      body: JSON.stringify(syncId),
    }).then(async (res) => {
      if (!res.ok) {
        throw new Error(await res.text());
      }
      return res.json();
    })
  );

  const cancelHistoryPut = useMutation(({ iboNumber, wwgId }) =>
    makeAuthedRequest(`/v1/topics/history/complete`, {
      method: "PUT",
      body: JSON.stringify({ iboNumber, wwgId }),
    })
  );

  const updateContactInfo = useMutation(
    async (contact) => {
      const res = await makeAuthedRequest(`/v1/topics/user/info`, {
        method: "PUT",
        body: JSON.stringify(contact),
      });

      if (!res.ok) {
        const responseText = await res.text();
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.message || "An error occurred.");
        } catch (err) {
          throw new Error(responseText || "Unknown error occurred.");
        }
      }

      return {
        status: res.status,
        data: res.status === 204 ? null : await res.json(),
      };
    },
    {
      onSuccess: (result) => {
        if (result.status === 204) {
          queryClient.invalidateQueries(["TopicsUser", iboNumber]);
        }
      },
      onError: (error) => {
        console.error("Error in updateContactInfo mutation:", error.message);
      },
    }
  );

  const retrieveAccessCode = useCallback(
    async (contact) => {
      try {
        const res = await makeAuthedRequest(`/v1/topics/user/access/code`, {
          method: "POST",
          body: JSON.stringify({
            topicsId: contact.topicsInfo.topicsId.toString(),
          }),
        });

        const data = await res.json();

        if (!res.ok) {
          didRetrieveAccessCode(
            `ERROR: ${data?.message || "Unknown error"}`,
            contact
          );
          return;
        }

        if (!data?.accessCode) {
          didRetrieveAccessCode("NULL", contact);
        } else {
          didRetrieveAccessCode(data.accessCode, contact);
        }
      } catch (err) {
        didRetrieveAccessCode(
          `ERROR: ${err.message || "Network error"}`,
          contact
        );
      }
    },
    [makeAuthedRequest, didRetrieveAccessCode]
  );

  return {
    data,
    error,
    isLoading,
    addTopicsAccount,
    refetch,
    syncTopicsAccount,
    fetchIboSyncData,
    retrieveGroupUsers,
    retrieveThreadUsers,
    retrieveAccessCode,
    cancelSyncPut,
    cancelHistoryPut,
    updateContactInfo,
  };
}
